import { MapContext } from 'contexts/MapContext';
import * as Styled from './styles';
import { useTranslation } from 'react-i18next';
import * as RiIcons from 'react-icons/ri';
import { CustomTooltip } from 'components/Tooltip';
import React, { useContext } from 'react';
import { GoDotFill, GoTrash } from "react-icons/go";
import { IoIosArrowDown, IoIosArrowUp, IoIosColorPalette } from "react-icons/io";
import { BsDot, BsCursorText, BsArrowUpLeft } from "react-icons/bs";
import { FaEdit, FaUndo, FaRedo, FaMapMarkerAlt } from "react-icons/fa";
import type { ToolCategories } from 'contexts/@types/Map';
import { SwipeMenu } from 'screens/Map/components/SwipeMenu';
import { TranslateFeaturesMenu } from 'screens/Map/components/TranslateFeatures';
import { SplitFeaturesMenu } from 'screens/Map/components/SplitFeatures';
import { IntersectLayersMenu } from 'screens/Map/components/IntersectLayers';
import { ClipLayersMenu } from 'screens/Map/components/ClipLayers';
import { BufferToolMenu } from 'screens/Map/components/BufferTool';
import { DissolveLayersMenu } from 'screens/Map/components/DissolveLayers';
import { MergeFeaturesMenu } from 'screens/Map/components/MergeFeatures';
import { ColorMenu } from 'screens/Map/components/ColorMenu';

export function Tools() {
  const { t: translation } = useTranslation();

  const {
    handleChangeTool,
    selectedTool,
    handleEraserAll,
    colorDraw,
    handleUndo,
    handleRedo,
    drawings,
    amountActionsRedo,
    amountActions
  } = useContext(MapContext);

  const toolCategories: ToolCategories[] = [
    {
      category: 'Formatos',
      uuid: "79404336-658a-4637-848a-18ddb76342ed",
      tools: [
        {
          title: "Alterar Cor",
          tooltipText: "Selecione a cor para seus desenhos.",
          icon: IoIosColorPalette,
          action: "color",
          toggle: true,
          color: true,
          expandable: true,
        },
        {
          title: "Retângulo",
          tooltipText: "Desenhe um retângulo e veja sua área total.",
          icon: RiIcons.RiSquareLine,
          action: "square",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Circulo",
          tooltipText: "Desenhe um círculo, e a área será calculada.",
          icon: RiIcons.RiCircleLine,
          action: "circle",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Lápis",
          tooltipText: "Desenhe uma forma livre e visualize a área resultante do desenho.",
          icon: RiIcons.RiPencilLine,
          action: "pencil",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Ponto",
          tooltipText: "Coloque um ponto em uma coordenada específica do mapa",
          icon: BsDot,
          action: "dot",
          toggle: true,
          color: true,
          expandable: false,
        }
      ]
    },
    {
      category: 'Detalhes',
      uuid: "f471e341-f0ac-41f1-a951-2b9ad1b7a18b",
      tools: [
        {
          title: "Medir",
          tooltipText: "Desenhe linhas para medir a distância total entre os pontos.",
          icon: RiIcons.RiRulerLine,
          action: "ruler",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Seta",
          tooltipText: "Desenhe setas para melhor representar áreas no mapa.",
          icon: BsArrowUpLeft,
          action: "arrow",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Texto",
          tooltipText: "Escreva textos para melhor representar áreas no mapa.",
          icon: BsCursorText,
          action: "text",
          toggle: true,
          color: true,
          expandable: false,
        },
        {
          title: "Marcador",
          tooltipText: "Adicione um marcador em uma coordenada específica do mapa",
          icon: FaMapMarkerAlt,
          action: "marker",
          toggle: true,
          color: true,
          expandable: false,
        },
      ],
    },
    {
      category: 'Editar',
      uuid: "8d81775e-4d29-4bf8-a127-a24dd96ae925",
      tools: [
        {
          title: "Editar",
          tooltipText: "Selecione e edite desenhos no mapa",
          icon: FaEdit,
          action: "edit",
          toggle: true,
          color: false,
          expandable: false,
        },
        {
          title: "Desfazer",
          tooltipText: "Desfaça a última alteração realizada",
          icon: FaUndo,
          action: "undo",
          toggle: false,
          color: false,
          expandable: false,
          func: handleUndo,
        },
        {
          title: "Refazer",
          tooltipText: "Refaça a última alteração desfeita",
          icon: FaRedo,
          action: "redo",
          toggle: false,
          color: false,
          expandable: false,
          func: handleRedo,
        },
      ],
    },
    {
      category: 'Apagar',
      uuid: "0cdcf13e-a202-4d3c-98d5-677229b59f53",
      tools: [
        {
          title: "Apagar",
          tooltipText: "Clique em um desenho para removê-lo do mapa.",
          icon: RiIcons.RiEraserLine,
          action: "eraser",
          toggle: true,
          color: false,
          expandable: false,
        },
        {
          title: "Apagar tudo",
          tooltipText: "Remove todos os desenhos e medições do mapa.",
          icon: GoTrash,
          action: "eraserAll",
          toggle: false,
          color: false,
          expandable: false,
          func: handleEraserAll
        },
      ],
    },
    {
      category: 'Gis (Camadas Gerais)',
      uuid: "9c2f7b6e-3e5d-4c8a-b2c4-8c4c0f2b7a91",
      tools: [
        {
          title: "Comparar",
          tooltipText: "Comparar visualmente duas camadas de dados sobrepostas",
          icon: RiIcons.RiSplitCellsVertical,
          action: "layerSwipe",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Interseção",
          tooltipText: "Cria uma nova camada com feições que se intersectam entre duas camadas vetoriais",
          icon: RiIcons.RiStackLine,
          action: "intersectLayers",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Buffer",
          tooltipText: "Cria uma nova camada de buffer ao clicar em uma feição",
          icon: RiIcons.RiFocus3Line,
          action: "buffer",
          color: false,
          expandable: true,
          toggle: true,
        },
      ],
    },
    {
      category: 'Gis (Camadas do usuário)',
      uuid: "cad6d511-3dec-49a9-af6c-c2922faf8469",
      tools: [
        {
          title: "Mover",
          tooltipText: "Mover features vetoriais selecionadas no mapa",
          icon: RiIcons.RiDragMove2Line,
          action: "translate",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Dividir",
          tooltipText: "Divide uma feição geométrica em múltiplas partes com base em uma linha de corte",
          icon: RiIcons.RiScissorsCutLine,
          action: "split",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Unir",
          tooltipText: "Combina duas feições vetoriais selecionadas em uma única geometria",
          icon: RiIcons.RiMergeCellsVertical,
          action: "merge",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Clip",
          tooltipText: "Recorta uma camada de entrada usando outra camada como limite",
          icon: RiIcons.RiCropLine,
          action: "clipLayers",
          color: false,
          expandable: true,
          toggle: true,
        },
        {
          title: "Dissolver",
          tooltipText: "Dissolve polígonos adjacentes com os mesmos atributos",
          icon: RiIcons.RiShape2Line,
          action: "dissolve",
          color: false,
          expandable: true,
          toggle: true,
        }
      ],
    },
  ];

  return (
    <Styled.Wrapper>
      <header>
        <strong>{translation('mapPage.sideBar.tools.title')}</strong>
      </header>

      <Styled.Content>
        {toolCategories.map(({ category, tools, uuid }) => (
          <React.Fragment key={uuid}>
            <div className='category-title'>
              <GoDotFill /><strong> {category} </strong>
            </div>

            <Styled.Container>
              {tools.map((tool, index) => {
                const IconOptTool = tool.icon;

                const handleDisabledAction = () => {
                  const isEditOrDelete =
                    uuid === "8d81775e-4d29-4bf8-a127-a24dd96ae925" ||
                    uuid === "0cdcf13e-a202-4d3c-98d5-677229b59f53";

                  const isRedo =
                    uuid === "8d81775e-4d29-4bf8-a127-a24dd96ae925" &&
                    tool.action === "redo";

                  const isUndo =
                    uuid === "8d81775e-4d29-4bf8-a127-a24dd96ae925" &&
                    tool.action === "undo";

                  if (isRedo) return amountActionsRedo <= 0;
                  if (isUndo) return amountActions <= 0;
                  if (isEditOrDelete) return drawings.length <= 0;

                  return false;
                };

                return (
                  <div key={`${uuid}-${index}`}>
                    <CustomTooltip title={tool.tooltipText} placement="top" disableInteractive>
                      <div>
                        <Styled.ToolsButton
                          selected={selectedTool?.action === tool.action}
                          onClick={() => {
                            if (tool.toggle) handleChangeTool(tool);
                            if (tool.func) tool.func();
                          }}
                          disabled={handleDisabledAction()}
                        >
                          <div className="icons-sidebar" style={{color: tool.color? colorDraw : 'black'}}>
                            <IconOptTool />
                          </div>
                          <span>{tool.title}</span>
                          { tool.expandable && (selectedTool?.action === tool.action? <IoIosArrowUp className="dropdown-arrow" /> : <IoIosArrowDown className="dropdown-arrow" />) }
                        </Styled.ToolsButton>
                      </div>
                    </CustomTooltip>

                    {tool.action === 'color' && selectedTool?.action === 'color' && (
                      <ColorMenu />
                    )}
                    {tool.action === 'layerSwipe' && selectedTool?.action === 'layerSwipe' && (
                      <SwipeMenu />
                    )}
                    {tool.action === 'translate' && selectedTool?.action === 'translate' && (
                      <TranslateFeaturesMenu />
                    )}
                     {tool.action === 'split' && selectedTool?.action === 'split' && (
                      <SplitFeaturesMenu />
                    )}
                     {tool.action === 'merge' && selectedTool?.action === 'merge' && (
                      <MergeFeaturesMenu />
                    )}
                    {tool.action === 'intersectLayers' && selectedTool?.action === 'intersectLayers' && (
                      <IntersectLayersMenu />
                    )}
                    {tool.action === 'clipLayers' && selectedTool?.action === 'clipLayers' && (
                      <ClipLayersMenu />
                    )}
                    {tool.action === 'buffer' && selectedTool?.action === 'buffer' && (
                      <BufferToolMenu />
                    )}
                    {tool.action === 'dissolve' && selectedTool?.action === 'dissolve' && (
                      <DissolveLayersMenu />
                    )}
                  </div>
                );
              })}
            </Styled.Container>
          </React.Fragment>
        ))}
      </Styled.Content>
    </Styled.Wrapper>
  );
}
